import {Tabs} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {bffApi} from "../../chat/bffApi";
import {FunksjonalitetTilgangId} from "../../../types";
import {ChecklistModal} from "../checklist/ChecklistModal";
import {GenerateChecklistTab} from "./GenerateChecklistTab";
import {GenerateTemplatesTab} from "./GenerateTemplatesTab";
import {SendToDflowTab} from "./SendToDflowTab";
import {ModalType} from "./types";
import type {CollectionMeta, ExportDocument, TopicMeta} from "./types";
import type {Status} from "../types";
import classes from "../checklist/modalStyles.module.css";

type Props = {
    opened: boolean;
    onClose: () => void;
    type: ModalType;
    topicMeta: TopicMeta;
    collectionMeta?: CollectionMeta;
    documents: ExportDocument[];
    labels?: string[];
    status?: (string | Status)[];
};

const TAB_CHECKLIST = "checklist";
const TAB_DOCUMENTS = "documents";
const TAB_DFLOW = "dokumentflyt";

const tabsClassNames = {
    list: classes.modalTabsList,
    tab: classes.modalTab,
    panel: classes.modalTabsPanel,
};

const getStartTab = (type: ModalType): string => {
    switch (type) {
        case ModalType.generateChecklist:
            return TAB_CHECKLIST;
        case ModalType.sendToDokumentflyt:
            return TAB_DFLOW;
        default:
            return TAB_DOCUMENTS;
    }
};

export const GenerateModal = ({
    opened,
    onClose,
    type,
    topicMeta,
    collectionMeta,
    documents,
    labels,
    status,
}: Props) => {
    const {t} = useTranslation();
    const {data: user} = bffApi.endpoints.getUser.useQuery();
    const hasDflow = !!user?.tilganger?.some((t) =>
        t.produkter?.some((p) => p.id === FunksjonalitetTilgangId.Dflow)
    );

    return (
        <ChecklistModal
            opened={opened}
            onClose={onClose}
            title={t("Action.Generate")}
        >
            <Tabs
                defaultValue={getStartTab(type)}
                classNames={tabsClassNames}
            >
                <Tabs.List>
                    <Tabs.Tab value={TAB_CHECKLIST}>
                        {t("Word.Checklist")}
                    </Tabs.Tab>
                    <Tabs.Tab value={TAB_DOCUMENTS}>
                        {t("Word.Templates")}
                    </Tabs.Tab>
                    {hasDflow && (
                        <Tabs.Tab value={TAB_DFLOW}>{t("name.Dflow")}</Tabs.Tab>
                    )}
                </Tabs.List>
                <Tabs.Panel value={TAB_CHECKLIST}>
                    <GenerateChecklistTab
                        onClose={onClose}
                        topicMeta={topicMeta}
                        collectionMeta={collectionMeta}
                        labels={labels}
                        status={status}
                    />
                </Tabs.Panel>
                <Tabs.Panel value={TAB_DOCUMENTS}>
                    <GenerateTemplatesTab
                        onClose={onClose}
                        topicMeta={topicMeta}
                        collectionMeta={collectionMeta}
                        documents={documents}
                    />
                </Tabs.Panel>
                {hasDflow && (
                    <Tabs.Panel value={TAB_DFLOW}>
                        <SendToDflowTab
                            onClose={onClose}
                            topicMeta={topicMeta}
                            collectionMeta={collectionMeta}
                            documents={documents}
                        />
                    </Tabs.Panel>
                )}
            </Tabs>
        </ChecklistModal>
    );
};
